import {
  AdditiveBlending,
  AmbientLight,
  CanvasTexture,
  Group,
  Light,
  PointLight,
  RectAreaLight,
  Sprite,
  SpriteMaterial,
  SRGBColorSpace,
  type Scene,
} from 'three'
import { gsap } from 'gsap'
import { RectAreaLightUniformsLib } from 'three/examples/jsm/lights/RectAreaLightUniformsLib.js'
import type { Vector3Config } from '../config/appConfig'

interface SceneLightingConfig {
  readonly ambientColor: number
  readonly ambientIntensity: number
}

interface AreaLightConfig {
  readonly color: number
  readonly intensity: number
  readonly width: number
  readonly height: number
  readonly position: Vector3Config
  readonly target: Vector3Config
}

interface PracticalLightingConfig {
  readonly wallWash: AreaLightConfig
  readonly paintingFill: AreaLightConfig
  readonly clockGlow: {
    readonly color: number
    readonly intensity: number
    readonly distance: number
    readonly decay: number
    readonly position: Vector3Config
    readonly haloPosition: Vector3Config
    readonly haloSize: number
    readonly haloOpacity: number
  }
  readonly bedsideLamps: {
    readonly color: number
    readonly intensity: number
    readonly distance: number
    readonly decay: number
    readonly positions: readonly Vector3Config[]
  }
}

const setPosition = (
  object: { position: { set: (x: number, y: number, z: number) => void } },
  position: Vector3Config,
): void => object.position.set(position.x, position.y, position.z)

export class LightingRig {
  private readonly root = new Group()
  private readonly disposableResources: Array<{ dispose: () => void }> = []
  private readonly lightLevels: Array<{ light: Light; intensity: number }> = []
  private readonly intensityState = { scale: 1 }
  private fadeTween: gsap.core.Tween | null = null
  private fadeResolve: (() => void) | null = null

  public constructor(
    scene: Scene,
    sceneConfig: SceneLightingConfig,
    lightingConfig: PracticalLightingConfig,
  ) {
    this.root.name = 'NightLightingRig'

    RectAreaLightUniformsLib.init()

    const ambient = new AmbientLight(sceneConfig.ambientColor, sceneConfig.ambientIntensity)
    ambient.name = 'SoftNightAmbient'
    this.root.add(ambient)
    this.trackLight(ambient)

    const wallWash = new RectAreaLight(
      lightingConfig.wallWash.color,
      lightingConfig.wallWash.intensity,
      lightingConfig.wallWash.width,
      lightingConfig.wallWash.height,
    )
    wallWash.name = 'WarmWallAreaLight'
    setPosition(wallWash, lightingConfig.wallWash.position)
    wallWash.lookAt(
      lightingConfig.wallWash.target.x,
      lightingConfig.wallWash.target.y,
      lightingConfig.wallWash.target.z,
    )
    this.root.add(wallWash)
    this.trackLight(wallWash)

    const paintingFill = new RectAreaLight(
      lightingConfig.paintingFill.color,
      lightingConfig.paintingFill.intensity,
      lightingConfig.paintingFill.width,
      lightingConfig.paintingFill.height,
    )
    paintingFill.name = 'PaintingAreaLight'
    setPosition(paintingFill, lightingConfig.paintingFill.position)
    paintingFill.lookAt(
      lightingConfig.paintingFill.target.x,
      lightingConfig.paintingFill.target.y,
      lightingConfig.paintingFill.target.z,
    )
    this.root.add(paintingFill)
    this.trackLight(paintingFill)

    const clockGlow = new PointLight(
      lightingConfig.clockGlow.color,
      lightingConfig.clockGlow.intensity,
      lightingConfig.clockGlow.distance,
      lightingConfig.clockGlow.decay,
    )
    clockGlow.name = 'WallClockPracticalLight'
    setPosition(clockGlow, lightingConfig.clockGlow.position)
    this.root.add(clockGlow)
    this.trackLight(clockGlow)

    if (lightingConfig.clockGlow.haloOpacity > 0) {
      const clockHaloTexture = this.createClockHaloTexture()
      const clockHaloMaterial = new SpriteMaterial({
        map: clockHaloTexture,
        color: lightingConfig.clockGlow.color,
        opacity: lightingConfig.clockGlow.haloOpacity,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        blending: AdditiveBlending,
        toneMapped: false,
      })
      const clockHalo = new Sprite(clockHaloMaterial)
      clockHalo.name = 'WallClockVisibleHalo'
      setPosition(clockHalo, lightingConfig.clockGlow.haloPosition)
      clockHalo.scale.setScalar(lightingConfig.clockGlow.haloSize)
      this.root.add(clockHalo)
      this.disposableResources.push(clockHaloTexture, clockHaloMaterial)
    }

    lightingConfig.bedsideLamps.positions.forEach((position, index) => {
      const lamp = new PointLight(
        lightingConfig.bedsideLamps.color,
        lightingConfig.bedsideLamps.intensity,
        lightingConfig.bedsideLamps.distance,
        lightingConfig.bedsideLamps.decay,
      )
      lamp.name = index === 0 ? 'LeftBedsidePracticalLight' : 'RightBedsidePracticalLight'
      setPosition(lamp, position)
      this.root.add(lamp)
      this.trackLight(lamp)
    })

    scene.add(this.root)
  }

  public fadeTo(intensityScale: number, durationMs: number): Promise<void> {
    this.cancelFade()
    const targetScale = Math.min(1, Math.max(0, intensityScale))
    const duration = Math.max(0, durationMs) / 1000

    if (duration === 0) {
      this.intensityState.scale = targetScale
      this.applyIntensityScale()
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      this.fadeResolve = resolve
      this.fadeTween = gsap.to(this.intensityState, {
        scale: targetScale,
        duration,
        ease: 'power2.inOut',
        onUpdate: this.applyIntensityScale,
        onComplete: () => {
          this.fadeTween = null
          this.fadeResolve = null
          this.applyIntensityScale()
          resolve()
        },
      })
    })
  }

  public dispose(scene: Scene): void {
    this.cancelFade()
    scene.remove(this.root)
    this.root.clear()
    this.lightLevels.length = 0
    this.disposableResources.forEach((resource) => resource.dispose())
    this.disposableResources.length = 0
  }

  private trackLight(light: Light): void {
    this.lightLevels.push({ light, intensity: light.intensity })
  }

  private readonly applyIntensityScale = (): void => {
    for (const entry of this.lightLevels) {
      entry.light.intensity = entry.intensity * this.intensityState.scale
    }
  }

  private cancelFade(): void {
    const resolve = this.fadeResolve
    this.fadeTween?.kill()
    this.fadeTween = null
    this.fadeResolve = null
    resolve?.()
  }

  private createClockHaloTexture(): CanvasTexture {
    const canvas = document.createElement('canvas')
    canvas.width = 128
    canvas.height = 128
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Could not create the clock glow texture.')

    const gradient = context.createRadialGradient(64, 64, 10, 64, 64, 64)
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0)')
    gradient.addColorStop(0.24, 'rgba(255, 255, 255, 0.04)')
    gradient.addColorStop(0.46, 'rgba(255, 255, 255, 0.86)')
    gradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.18)')
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
    context.fillStyle = gradient
    context.fillRect(0, 0, 128, 128)

    const texture = new CanvasTexture(canvas)
    texture.name = 'WallClockHaloTexture'
    texture.colorSpace = SRGBColorSpace
    return texture
  }
}
